import {
  Box,
  Button,
  Checkbox,
  Divider,
  InputAdornment,
  InputBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {useTranslation} from 'react-i18next';
import {type ComponentProps, useEffect, useState, useRef} from 'react';
import {ChipButton} from 'components/Generic/Buttons/ChipButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export function MultiSelectChip({
  label,
  options,
  selection,
  setSelection,
  onClose,
  onReset,
  align = 'left',
  alignTransform = 'left',
  translateOptions,
  dataTestId,
  embeded,
  optionNamesMap,
  optionsWithPaddingLeft,
  enableSearch,
  slots,
  slotProps,
}: MultiSelectProps) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const {t} = useTranslation('common');
  const [searchValue, setSearchValue] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  const closeMenu = () => {
    setAnchorEl(null);
    if (onClose) {
      onClose();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const visibleOptions = enableSearch
    ? options.filter((option) => {
        const optionName = optionNamesMap?.get(option) ?? option;

        return optionName.trim().toLowerCase().includes(searchValue.trim().toLowerCase());
      })
    : options;

  const buttonSX: ComponentProps<typeof Button>['sx'] = embeded
    ? {
        width: '100%',
        padding: '4px 8px',
      }
    : undefined;

  useEffect(() => {
    if (options.length > 0 && selection.length > 0) {
      const optionsCanbeFiltered = selection.filter((i) => options.includes(i));
      if (optionsCanbeFiltered.length === 0) {
        setSelection([]);
      }
    }
  }, [options]);

  const MenuHeader = slots?.menuHeader;

  return (
    <>
      <Stack
        sx={buttonSX}
        direction={'row'}
        alignItems={'center'}
        justifyContent={embeded ? 'space-between' : 'auto'}
        spacing={0.4}
      >
        <ChipButton
          aria-haspopup="true"
          endIcon={<KeyboardArrowDownIcon />}
          size="small"
          color="inherit"
          onClick={handleClick}
          data-testid={dataTestId}
        >
          {label}
          <Stack direction="row">
            {selection.length > 0 && (
              <FilterCounter mx={0.5} variant="body2" width={20} align="center">
                {selection.length}
              </FilterCounter>
            )}
          </Stack>
        </ChipButton>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        style={{zIndex: 3000}}
        anchorOrigin={{horizontal: align, vertical: embeded ? 'top' : 'bottom'}}
        transformOrigin={{horizontal: alignTransform, vertical: 'top'}}
        slotProps={{
          list: {
            sx: {
              pt: (enableSearch ?? MenuHeader) ? 0 : 1,
            },
          },
        }}
      >
        {MenuHeader && <MenuHeader {...slotProps?.menuHeader} />}
        {enableSearch && (
          <InputBase
            fullWidth
            value={searchValue}
            onChange={handleSearchChange}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              e.stopPropagation();

              if (e.key === 'ArrowDown' || e.key === 'Enter') {
                e.preventDefault();
                const firstListElement = listRef.current?.firstChild;

                if (firstListElement instanceof HTMLElement) {
                  firstListElement.focus();
                }
              }
            }}
            placeholder={t('search')}
            autoFocus
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon fontSize="inherit" color="secondary" />
              </InputAdornment>
            }
            sx={{px: 2, py: 1, backgroundColor: (theme) => theme.palette.background.default}}
          />
        )}
        {(enableSearch ?? MenuHeader) && <Divider />}
        <Box sx={{maxHeight: 296, overflowY: 'auto'}} ref={listRef}>
          {visibleOptions.map((option) => (
            <MenuItem
              key={option}
              onClick={() => {
                if (selection.includes(option)) {
                  setSelection(selection.filter((i) => i !== option));
                } else {
                  setSelection([...selection, option]);
                }
              }}
              data-testid={`${dataTestId}-item-${option}`}
              sx={{paddingLeft: optionsWithPaddingLeft?.includes(option) ? 4 : ''}}
            >
              <ListItemIcon>
                <Checkbox sx={{p: 0}} disableRipple checked={selection.includes(option)} />
              </ListItemIcon>
              <ListItemText>{translateOptions ? t(option) : (optionNamesMap?.get(option) ?? option)}</ListItemText>
            </MenuItem>
          ))}
        </Box>
        <Stack direction="row" mx={1} marginTop={1}>
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              setSelection([]);
              onReset?.();
              closeMenu();
            }}
            size="small"
            data-testid="clear"
          >
            {t('clear')}
          </Button>
        </Stack>
      </Menu>
    </>
  );
}

export const FilterCounter = styled(Typography)(({theme}) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: 40,
  color: theme.palette.background.paper,
  width: 24,
}));

interface MultiSelectProps<S extends {menuHeader?: React.ElementType} = Record<string, React.ElementType>> {
  label: string;
  options: string[];
  selection: string[];
  setSelection: (newSelection: string[]) => void;
  onClose?: () => void;
  onReset?: () => void;
  align?: 'left' | 'right';
  alignTransform?: 'left' | 'right';
  color?: (option: string) => string;
  translateOptions?: boolean;
  dataTestId?: string;
  embeded?: boolean;
  enableSearch?: boolean;
  optionNamesMap?: Map<string, string>; // yes, that ugly due to backward compatibility
  optionsWithPaddingLeft?: string[];
  slots?: S;
  slotProps?: {
    [K in keyof S]: S[K] extends React.ElementType ? ComponentProps<S[K]> : never;
  };
}
